<template>
  <div class="pp" :class="'pp--' + variant">
    <!-- Wolt: image hero and product options -->
    <template v-if="variant === 'wolt'">
      <div class="pp__hero">
        <img v-if="product.image_url" :src="product.image_url" :alt="product.name">
        <div v-else class="pp__hero-placeholder"></div>
        <button type="button" class="pp__x pp__x--red" aria-label="close" @click="$emit('close')">×</button>
      </div>
      <div class="pp__scroll">
        <h3 class="pp__title">{{ product.name }}</h3>
        <div class="pp__price-row">
          <span class="pp__price-badge">{{ formatPrice(product.price) }}</span>
        </div>
        <p v-if="product.description" class="pp__desc">{{ product.description }}</p>
        <div
          v-for="option in (product.options || [])"
          :key="option.id"
          class="pp__option"
        >
          <div class="pp__option-head">
            <strong>{{ option.name }}</strong>
            <small>{{ $t('platformMenuImport.groupMin') }}: {{ option.min }} · {{ $t('platformMenuImport.groupMax') }}: {{ option.max }}</small>
          </div>
          <label
            v-for="value in (option.values || [])"
            :key="value.id"
            class="pp__value"
            :class="{ 'is-disabled': value.available === false }"
          >
            <input v-if="option.counter" type="number" min="0" :max="value.max_quantity || option.max" :value="counterQty(option, value.id)" :disabled="value.available === false || !product.available" @input="setCounter(option, value, $event.target.value)">
            <input
              v-else-if="option.multiple"
              type="checkbox"
              :name="'wolt-opt-' + option.id"
              :value="value.id"
              :disabled="value.available === false || !product.available"
              v-model="selections[option.id]"
            >
            <input
              v-else
              type="radio"
              :name="'wolt-opt-' + option.id"
              :value="value.id"
              :disabled="value.available === false || !product.available"
              v-model="selections[option.id]"
            >
            <span class="pp__value-name">{{ value.name }}<small v-if="value.max_quantity != null"> · {{ $t('platformMenuImport.eachMax') }}: {{ value.max_quantity }}</small><small v-if="value.deposit"> · {{ value.deposit.name || 'SGR' }}: {{ formatPrice(value.deposit.price) }}</small></span>
            <span class="pp__value-price">+{{ formatPrice(value.price || 0) }}</span>
          </label>
        </div>
      </div>
    </template>

    <!-- Bolt: image hero and product options -->
    <template v-else-if="variant === 'bolt'">
      <div class="pp__hero">
        <img v-if="product.image_url" :src="product.image_url" :alt="product.name">
        <div v-else class="pp__hero-placeholder"></div>
        <button type="button" class="pp__x pp__x--red" aria-label="close" @click="$emit('close')">×</button>
      </div>
      <div class="pp__scroll">
        <h3 class="pp__title">{{ product.name }}</h3>
        <p class="pp__price-plain">{{ formatPrice(product.price) }}</p>
        <p v-if="product.description" class="pp__desc">{{ product.description }}</p>
        <div
          v-for="option in (product.options || [])"
          :key="option.id"
          class="pp__option"
        >
          <div class="pp__option-head">
            <strong>{{ option.name }}</strong>
            <small>{{ $t('platformMenuImport.groupMin') }}: {{ option.min }} · {{ $t('platformMenuImport.groupMax') }}: {{ option.max }}</small>
            <span v-if="option.counter" class="pp__badge pp__badge--counter">
              {{ $t('gestiune.integrationsMenuPreviewOptionRange', { min: option.min || 0, max: option.max || 0 }) }}
            </span>
            <span v-else-if="option.min >= 1" class="pp__badge pp__badge--required">
              {{ $t('gestiune.integrationsMenuPreviewRequiredShort') }}
            </span>
          </div>
          <template v-if="option.counter">
            <div
              v-for="value in (option.values || [])"
              :key="value.id"
              class="pp__value pp__value--counter"
              :class="{ 'is-disabled': value.available === false }"
            >
              <span class="pp__value-name">{{ value.name }}<small v-if="value.max_quantity != null"> · {{ $t('platformMenuImport.eachMax') }}: {{ value.max_quantity }}</small><small v-if="value.deposit"> · {{ value.deposit.name || 'SGR' }}: {{ formatPrice(value.deposit.price) }}</small></span>
              <span class="pp__value-price">+{{ formatPrice(value.price || 0) }}</span>
              <div class="pp__qty pp__qty--option">
                <button
                  type="button"
                  :disabled="value.available === false || !product.available || counterQty(option, value.id) <= 0"
                  @click="adjustCounter(option, value.id, -1)"
                >−</button>
                <span>{{ counterQty(option, value.id) }}</span>
                <button
                  type="button"
                  :disabled="value.available === false || !product.available || !canIncreaseCounter(option, value.id)"
                  @click="adjustCounter(option, value.id, 1)"
                >+</button>
              </div>
            </div>
          </template>
          <template v-else>
            <label
              v-for="value in (option.values || [])"
              :key="value.id"
              class="pp__value"
              :class="{ 'is-disabled': value.available === false }"
            >
              <input
                v-if="option.multiple"
                type="checkbox"
                :name="'bolt-opt-' + option.id"
                :value="value.id"
                :disabled="value.available === false || !product.available"
                v-model="selections[option.id]"
              >
              <input
                v-else
                type="radio"
                :name="'bolt-opt-' + option.id"
                :value="value.id"
                :disabled="value.available === false || !product.available"
                v-model="selections[option.id]"
              >
              <span class="pp__value-name">{{ value.name }}<small v-if="value.max_quantity != null"> · {{ $t('platformMenuImport.eachMax') }}: {{ value.max_quantity }}</small><small v-if="value.deposit"> · {{ value.deposit.name || 'SGR' }}: {{ formatPrice(value.deposit.price) }}</small></span>
              <span class="pp__value-price">+{{ formatPrice(value.price || 0) }}</span>
            </label>
          </template>
        </div>
      </div>
    </template>

    <!-- Glovo: two columns -->
    <template v-else>
      <button type="button" class="pp__x pp__x--red pp__x--glovo" aria-label="close" @click="$emit('close')">×</button>
      <div class="pp__glovo">
        <div class="pp__glovo-left">
          <div
            v-for="option in (product.options || [])"
            :key="option.id"
            class="pp__glovo-option"
          >
            <div class="pp__glovo-option-head">
              <div>
                <strong>{{ option.name }}</strong>
            <small>{{ $t('platformMenuImport.groupMin') }}: {{ option.min }} · {{ $t('platformMenuImport.groupMax') }}: {{ option.max }}</small>
                <div class="pp__glovo-hint">{{ optionHint(option) }}</div>
              </div>
              <span v-if="option.min >= 1" class="pp__badge pp__badge--glovo">
                {{ $t('gestiune.integrationsMenuPreviewOptionRequired') }}
              </span>
            </div>
            <label
              v-for="value in (option.values || [])"
              :key="value.id"
              class="pp__glovo-value"
              :class="{
                'is-disabled': value.available === false,
                'is-selected': isSelected(option, value.id)
              }"
            >
              <span class="pp__value-name">{{ value.name }}<small v-if="value.max_quantity != null"> · {{ $t('platformMenuImport.eachMax') }}: {{ value.max_quantity }}</small><small v-if="value.deposit"> · {{ value.deposit.name || 'SGR' }}: {{ formatPrice(value.deposit.price) }}</small></span>
              <span v-if="value.price" class="pp__value-price">+{{ formatPrice(value.price) }}</span>
              <input
                v-if="option.multiple"
                type="checkbox"
                :name="'glovo-opt-' + option.id"
                :value="value.id"
                :disabled="value.available === false || !product.available"
                v-model="selections[option.id]"
              >
              <input
                v-else
                type="radio"
                :name="'glovo-opt-' + option.id"
                :value="value.id"
                :disabled="value.available === false || !product.available"
                v-model="selections[option.id]"
              >
            </label>
          </div>
          <p v-if="!(product.options || []).length" class="pp__glovo-empty">
            {{ product.description || '—' }}
          </p>
        </div>
        <div class="pp__glovo-right">
          <div class="pp__glovo-media">
            <img v-if="product.image_url" :src="product.image_url" :alt="product.name">
            <div v-else class="pp__hero-placeholder"></div>
          </div>
          <h3 class="pp__title">{{ product.name }}</h3>
          <p class="pp__price-plain">{{ formatPrice(product.price) }}</p>
          <p v-if="product.description" class="pp__desc">{{ product.description }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ProductOptionsPanel',
  props: {
    product: { type: Object, required: true },
    currency: { type: String, default: 'RON' },
    variant: { type: String, default: 'wolt' }
  },
  data () {
    return {
      selections: {}
    }
  },
  watch: {
    product: {
      immediate: true,
      handler (product) {
        const next = {}
        ;(product.options || []).forEach((option) => {
          if (option.counter) {
            next[option.id] = {}
            ;(option.values || []).filter(v => v.selected_by_default).forEach(v => { next[option.id][v.id] = 1 })
          } else if (option.multiple) {
            next[option.id] = (option.values || []).filter(v => v.selected_by_default).map(v => v.id)
          } else {
            const values = option.values || []
            const first = values.find(v => v.selected_by_default && v.available !== false) || (values.some(v => v.selected_by_default != null) ? null : values.find(v => v.available !== false))
            next[option.id] = first ? first.id : null
          }
        })
        this.selections = next
      }
    }
  },
  methods: {
    setCounter (option, value, input) {
      const limit = Math.max(0, Number(option.max) - this.counterTotal(option) + this.counterQty(option, value.id))
      const count = Math.max(0, Math.min(Number(input) || 0, Number(value.max_quantity) || limit, limit))
      const selected = Object.assign({}, this.selections[option.id] || {})
      selected[value.id] = Math.floor(count)
      this.$set(this.selections, option.id, selected)
    },
    formatPrice (price) {
      const n = Number(price)
      if (Number.isNaN(n)) return ''
      return n.toFixed(2).replace('.', ',') + ' ' + this.currency
    },
    optionHint (option) {
      const min = Number(option.min || 0)
      const max = Number(option.max || 0)
      if (option.multiple) {
        return this.$t('gestiune.integrationsMenuPreviewOptionRange', { min, max })
      }
      if (min >= 1) {
        return this.$t('gestiune.integrationsMenuPreviewChooseOne')
      }
      return this.$t('gestiune.integrationsMenuPreviewOptionOptional')
    },
    isSelected (option, valueId) {
      const selected = this.selections[option.id]
      if (option.multiple && Array.isArray(selected)) {
        return selected.map(String).includes(String(valueId))
      }
      return String(selected) === String(valueId)
    },
    counterQty (option, valueId) {
      const selected = this.selections[option.id]
      if (!selected || typeof selected !== 'object') return 0
      return Number(selected[valueId]) || 0
    },
    counterTotal (option) {
      const selected = this.selections[option.id]
      if (!selected || typeof selected !== 'object') return 0
      return Object.keys(selected).reduce((sum, id) => sum + (Number(selected[id]) || 0), 0)
    },
    canIncreaseCounter (option, valueId) {
      const value = (option.values || []).find(v => String(v.id) === String(valueId))
      const eachMax = Math.max(1, Number(value && value.max_quantity) || Number(option.each_max) || 1)
      const groupMax = Math.max(1, Number(option.max) || eachMax)
      const current = this.counterQty(option, valueId)
      if (current >= eachMax) return false
      return this.counterTotal(option) < groupMax
    },
    adjustCounter (option, valueId, delta) {
      const current = this.counterQty(option, valueId)
      const nextQty = current + delta
      if (nextQty < 0) return
      if (delta > 0 && !this.canIncreaseCounter(option, valueId)) return

      const prev = this.selections[option.id] && typeof this.selections[option.id] === 'object'
        ? { ...this.selections[option.id] }
        : {}
      if (nextQty <= 0) {
        delete prev[valueId]
      } else {
        prev[valueId] = nextQty
      }
      this.$set(this.selections, option.id, prev)
    }
  }
}
</script>

<style scoped>
.pp {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  position: relative;
  overflow: hidden;
}
.pp__hero {
  position: relative;
  flex: 0 0 280px;
  height: 280px;
  max-height: 280px;
  min-height: 0;
  overflow: hidden;
  background: #f3f4f6;
}
.pp__hero img,
.pp__hero-placeholder {
  width: 100%;
  height: 100%;
  max-height: 280px;
  object-fit: contain;
  object-position: center;
  display: block;
}
.pp__hero-placeholder {
  background: linear-gradient(135deg, #e5e7eb, #f9fafb);
}
.pp__x {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 0;
  background: #fff;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0,0,0,.15);
}
.pp__x--red {
  color: #ff0d19;
  border: 2px solid #ff0d19;
}
.pp__x--glovo {
  top: 10px;
  right: 10px;
}
.pp__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 16px 20px 12px;
}
.pp__title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
}
.pp--wolt .pp__title {
  color: #0b1f33;
}
.pp__price-row {
  margin-bottom: 10px;
}
.pp__price-badge {
  display: inline-block;
  background: #f5c542;
  color: #111;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 14px;
}
.pp__price-plain {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 800;
}
.pp__desc {
  color: #6b7280;
  margin: 0 0 14px;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.4;
}
.pp__option {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}
.pp__option-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.pp__badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
}
.pp__badge--required {
  background: #e74c3c;
  color: #fff;
}
.pp__badge--counter {
  background: #ecfdf5;
  color: #047857;
}
.pp__badge--glovo {
  background: #f3f4f6;
  color: #6b7280;
}
.pp__value {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  cursor: pointer;
}
.pp__value--counter {
  cursor: default;
}
.pp__value.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pp__value-name { flex: 1; }
.pp__value-price { color: #6b7280; font-size: 13px; }
.pp__qty--option {
  background: #fff;
  border: 1px solid #e5e7eb;
  padding: 2px 6px;
  gap: 6px;
}
.pp__qty--option button:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.pp__qty {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 999px;
  padding: 6px 10px;
  flex: 0 0 auto;
}
.pp__qty button {
  border: 0;
  background: transparent;
  font-size: 18px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  line-height: 1;
}
.pp__qty span {
  min-width: 18px;
  text-align: center;
  font-weight: 700;
}
/* Glovo two-column */
.pp--glovo {
  padding-top: 0;
}
.pp__glovo {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
}
.pp__glovo-left,
.pp__glovo-right {
  min-height: 0;
  overflow: auto;
  padding: 16px 18px;
}
.pp__glovo-left {
  border-right: 1px solid #eee;
}
.pp__glovo-option {
  margin-bottom: 18px;
}
.pp__glovo-option-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 8px;
}
.pp__glovo-hint {
  color: #9ca3af;
  font-size: 12px;
  margin-top: 2px;
}
.pp__glovo-value {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
}
.pp__glovo-value.is-selected {
  background: #f3f4f6;
}
.pp__glovo-value input {
  margin-left: auto;
}
.pp__glovo-media {
  width: 100%;
  height: 260px;
  max-height: 260px;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  margin-bottom: 12px;
}
.pp__glovo-media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
}
.pp__glovo-actions {
  margin-top: 16px;
}
.pp__glovo-empty {
  color: #9ca3af;
  padding: 24px 0;
}
@media (max-width: 900px) {
  .pp__glovo {
    grid-template-columns: 1fr;
  }
  .pp__glovo-left {
    border-right: 0;
    border-bottom: 1px solid #eee;
    max-height: 45%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/MenuPreview/ProductOptionsPanel.vue